<template>
  <div class="medication-dosage-unit">
    <v-select
      :disabled="!isEditable"
      :items="items"
      v-model="computedValue"
      outlined
      hide-details
      dense
      class="medication-dosage-unit__select"
    >
      <template v-slot:item="{ item }">
        <span>{{ item.text }}</span>
        <medication-recommendation-tooltip
            v-if="recommendedDosageUnit && item.value === recommendedDosageUnit"
        />
      </template>
      <template v-slot:selection="{ item }">
        <span>{{ item.text }}</span>
        <medication-recommendation-tooltip
            v-if="hasUnitRecommendations && !hideRecommendationInfo"
            :icon="computedValue !== recommendedDosageUnit ? 'mdi-alert-circle' : null"
            :color="computedValue !== recommendedDosageUnit ? 'grey' : null"
            :value="computedValue !== recommendedDosageUnit ? null : ''"
        />
      </template>
    </v-select>
  </div>
</template>

<script>
import {INJECTION, PILL} from "./administration";
import MedicationRecommendationTooltip from "./MedicationRecommendationTooltip";
import {UCUMUnit} from "../../../../../helper/UCUMUnit";

/**
 * Component MedicationDosageUnit.vue
 *
 * @module MedicationDosageUnit
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.10] app/src/APP_DASHBOARD/templates/components/Parts/Medication/MedicationDosageUnit.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: 'MedicationDosageUnit',
  components: {MedicationRecommendationTooltip},
  props: {
    value: {
      type: String,
      required: true,
    },
    isEditable: {
      type: Boolean,
      required: true,
    },
    administration: {
      type: String,
      required: true,
    },
    recommendedUnits: {
      type: Array,
      default: () => []
    },
    hideRecommendationInfo: {
      type: Boolean,
      default: () => false
    },
    recommendedDosageUnit: {
      type: String,
      default: () => false
    },
  },
  computed: {
    hasUnitRecommendations() {
      return this.recommendedUnits?.length > 0;
    },
    items() {
      switch (this.administration) {
        case PILL:
          return UCUMUnit.generateTranslationLabelValueObjectsWithCatalogForUnits(
              this.$t,
              'text',
              UCUMUnit.IU,
              UCUMUnit.UG,
              UCUMUnit.MG,
              UCUMUnit.G,
              UCUMUnit.OTHER,
              UCUMUnit.UNKNOWN,
          );
        case INJECTION:
        default:
          return UCUMUnit.generateTranslationLabelValueObjectsWithCatalogForUnits(
              this.$t,
              'text',
              UCUMUnit.IU,
              UCUMUnit.UG,
              UCUMUnit.MG,
              UCUMUnit.ML,
              UCUMUnit.MG_ML,
              UCUMUnit.MG_IU,
              UCUMUnit.G,
              UCUMUnit.OTHER,
              UCUMUnit.UNKNOWN,
          );
      }
    },
    computedValue: {
      get() {
        // Check if value exists
        if (this.items.some(item => item.value === this.value)) {
          return this.value;
        } else {
          // Return default value based on administration
          let defaultValue;
          if (this.administration === PILL) {
            defaultValue = this.items.find(item => item.value == UCUMUnit.MG.unit).value || this.items[0];
            this.$emit('input', defaultValue);
            return defaultValue;
          } else {
            defaultValue = this.items.find(item => item.value == UCUMUnit.ML.unit).value || this.items[0];
            this.$emit('input', defaultValue);
            return defaultValue;
          }
        }
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
};
</script>

<style lang="scss">
.medication-dosage-unit__select {
  width: 160px;
}
</style>
